import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeftIcon,
  PlayIcon,
  PauseIcon,
  StopIcon,
  ClockIcon,
  MapPinIcon,
  ChartBarIcon,
  CalendarIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XMarkIcon,
  PlusIcon,
  MinusIcon
} from '@heroicons/react/24/outline';
import authFetch from '../../api.js';

const TimeTracker = () => {
  const [isTracking, setIsTracking] = useState(false);
  const [currentSession, setCurrentSession] = useState(null);
  const [timeLogs, setTimeLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [location, setLocation] = useState(null);
  const [showBreakModal, setShowBreakModal] = useState(false);
  const [breakType, setBreakType] = useState('lunch');
  const [selectedProject, setSelectedProject] = useState('');
  const [projects, setProjects] = useState([]);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [stats, setStats] = useState({
    totalHours: 0,
    totalBreaks: 0,
    overtimeHours: 0,
    weeklyHours: 0
  });
  const intervalRef = useRef(null);
  const navigate = useNavigate();

  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Get user location
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy
          });
        },
        (error) => {
          console.error('Error getting location:', error);
        }
      );
    }
  }, []);

  const fetchTimeData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('jwtToken');
      if (!token) {
        setError('No authentication token found. Please log in.');
        return;
      }

      const [timeLogsResponse, projectsResponse, statsResponse, currentSessionResponse] = await Promise.all([
        authFetch('/employee/time-logs'),
        authFetch('/projects/assigned'),
        authFetch('/employee/time-stats'),
        authFetch('/employee/current-session')
      ]);
      
      setTimeLogs(timeLogsResponse || []);
      setProjects(projectsResponse || []);
      setStats(statsResponse || {
        totalHours: 0,
        totalBreaks: 0,
        overtimeHours: 0,
        weeklyHours: 0
      });
      
      if (currentSessionResponse) {
        setCurrentSession(currentSessionResponse);
        setIsTracking(true);
      }
      
    } catch (error) {
      console.error('Error fetching time data:', error);
      if (error.response?.status === 401) {
        setError('Authentication failed. Please log in again.');
        localStorage.removeItem('jwtToken');
        localStorage.removeItem('user');
      } else {
        setError('Failed to load time data. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTimeData();
  }, [fetchTimeData]);

  const handleLogout = () => {
    localStorage.removeItem('jwtToken');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const startTracking = async () => {
    if (!selectedProject) {
      setShowProjectModal(true);
      return;
    }

    try {
      const sessionData = {
        projectId: selectedProject,
        startTime: new Date().toISOString(),
        location: location
      };

      const response = await authFetch('/employee/time-logs/start', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(sessionData)
      });

      setCurrentSession(response);
      setIsTracking(true);
      setShowProjectModal(false);
    } catch (error) {
      console.error('Error starting time tracking:', error);
      alert('Failed to start time tracking. Please try again.');
    }
  };

  const pauseTracking = async () => {
    try {
      await authFetch(`/employee/time-logs/${currentSession._id}/pause`, {
        method: 'PUT'
      });

      setCurrentSession(prev => ({
        ...prev,
        status: 'paused',
        pauseTime: new Date().toISOString()
      }));
    } catch (error) {
      console.error('Error pausing time tracking:', error);
      alert('Failed to pause time tracking. Please try again.');
    }
  };

  const resumeTracking = async () => {
    try {
      await authFetch(`/employee/time-logs/${currentSession._id}/resume`, {
        method: 'PUT'
      });

      setCurrentSession(prev => ({
        ...prev,
        status: 'active',
        resumeTime: new Date().toISOString()
      }));
    } catch (error) {
      console.error('Error resuming time tracking:', error);
      alert('Failed to resume time tracking. Please try again.');
    }
  };

  const stopTracking = async () => {
    try {
      const endTime = new Date().toISOString();
      const response = await authFetch(`/employee/time-logs/${currentSession._id}/stop`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          endTime: endTime,
          location: location
        })
      });

      setTimeLogs(prev => [response, ...prev]);
      setCurrentSession(null);
      setIsTracking(false);
      setSelectedProject('');
      
      // Refresh stats
      const statsResponse = await authFetch('/employee/time-stats');
      setStats(statsResponse || stats);
    } catch (error) {
      console.error('Error stopping time tracking:', error);
      alert('Failed to stop time tracking. Please try again.');
    }
  };

  const startBreak = async () => {
    try {
      const breakData = {
        type: breakType,
        startTime: new Date().toISOString(),
        location: location
      };

      await authFetch('/employee/breaks/start', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(breakData)
      });

      setShowBreakModal(false);
      setBreakType('lunch');
    } catch (error) {
      console.error('Error starting break:', error);
      alert('Failed to start break. Please try again.');
    }
  };

  const formatDuration = (startTime, endTime = null) => {
    const start = new Date(startTime);
    const end = endTime ? new Date(endTime) : new Date();
    const diff = end - start;
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);
    
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const formatTime = (time) => {
    return new Date(time).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'paused': return 'bg-yellow-100 text-yellow-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getBreakTypeIcon = (type) => {
    switch (type) {
      case 'lunch': return '🍽️';
      case 'coffee': return '☕';
      case 'rest': return '😴';
      default: return '⏸️';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex justify-center items-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-[#173861] border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Loading time tracker...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex justify-center items-center p-4">
        <div className="text-center max-w-md">
          <ExclamationTriangleIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <div className="text-red-600 text-lg mb-4">{error}</div>
          <div className="space-x-4">
            <button
              onClick={() => navigate('/employee/dashboard')}
              className="bg-[#173861] text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors duration-200"
            >
              Back to Dashboard
            </button>
            <button
              onClick={() => navigate('/login')}
              className="bg-gray-500 text-white px-6 py-3 rounded-lg hover:bg-gray-600 transition-colors duration-200"
            >
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/employee/dashboard')}
                className="p-2 text-gray-600 hover:text-gray-900 transition-colors duration-200"
              >
                <ArrowLeftIcon className="h-6 w-6" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Time Tracker</h1>
                <p className="text-sm text-gray-500">Track your work hours and breaks</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <button
                onClick={handleLogout}
                className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors duration-200"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Current Time Display */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="text-center">
            <div className="text-4xl font-bold text-gray-900 mb-2">
              {currentTime.toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: false
              })}
            </div>
            <div className="text-lg text-gray-600">
              {currentTime.toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </div>
          </div>
        </div>

        {/* Time Tracking Controls */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="text-center">
            {isTracking && currentSession ? (
              <div className="mb-6">
                <div className="text-3xl font-bold text-blue-600 mb-2">
                  {formatDuration(currentSession.startTime, currentSession.endTime)}
                </div>
                <div className="text-lg text-gray-600 mb-4">
                  Started at {formatTime(currentSession.startTime)}
                </div>
                <div className="flex items-center justify-center space-x-4 mb-4">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(currentSession.status)}`}>
                    {currentSession.status === 'active' ? 'Active' : 'Paused'}
                  </span>
                  {location && (
                    <div className="flex items-center text-sm text-gray-500">
                      <MapPinIcon className="h-4 w-4 mr-1" />
                      Location tracked
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="mb-6">
                <div className="text-3xl font-bold text-gray-400 mb-2">00:00:00</div>
                <div className="text-lg text-gray-500">Not tracking</div>
              </div>
            )}

            <div className="flex items-center justify-center space-x-4">
              {!isTracking ? (
                <button
                  onClick={startTracking}
                  className="bg-green-500 text-white px-8 py-3 rounded-lg hover:bg-green-600 transition-colors duration-200 flex items-center"
                >
                  <PlayIcon className="h-5 w-5 mr-2" />
                  Start Tracking
                </button>
              ) : (
                <>
                  {currentSession?.status === 'active' ? (
                    <button
                      onClick={pauseTracking}
                      className="bg-yellow-500 text-white px-8 py-3 rounded-lg hover:bg-yellow-600 transition-colors duration-200 flex items-center"
                    >
                      <PauseIcon className="h-5 w-5 mr-2" />
                      Pause
                    </button>
                  ) : (
                    <button
                      onClick={resumeTracking}
                      className="bg-blue-500 text-white px-8 py-3 rounded-lg hover:bg-blue-600 transition-colors duration-200 flex items-center"
                    >
                      <PlayIcon className="h-5 w-5 mr-2" />
                      Resume
                    </button>
                  )}
                  
                  <button
                    onClick={stopTracking}
                    className="bg-red-500 text-white px-8 py-3 rounded-lg hover:bg-red-600 transition-colors duration-200 flex items-center"
                  >
                    <StopIcon className="h-5 w-5 mr-2" />
                    Stop
                  </button>
                </>
              )}
              
              <button
                onClick={() => setShowBreakModal(true)}
                className="bg-purple-500 text-white px-6 py-3 rounded-lg hover:bg-purple-600 transition-colors duration-200 flex items-center"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                Break
              </button>
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ClockIcon className="h-8 w-8 text-blue-500" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Hours</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalHours.toFixed(1)}h</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ChartBarIcon className="h-8 w-8 text-green-500" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Weekly Hours</p>
                <p className="text-2xl font-bold text-gray-900">{stats.weeklyHours.toFixed(1)}h</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <MinusIcon className="h-8 w-8 text-orange-500" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Breaks</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalBreaks}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ExclamationTriangleIcon className="h-8 w-8 text-red-500" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Overtime</p>
                <p className="text-2xl font-bold text-gray-900">{stats.overtimeHours.toFixed(1)}h</p>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Time Logs */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Time Logs</h3>
          <div className="space-y-4">
            {timeLogs.length > 0 ? (
              timeLogs.slice(0, 10).map((log) => (
                <div key={log._id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-4">
                    <div className={`w-3 h-3 rounded-full ${
                      log.status === 'active' ? 'bg-green-500' :
                      log.status === 'paused' ? 'bg-yellow-500' : 'bg-blue-500'
                    }`}></div>
                    <div>
                      <h4 className="font-medium text-gray-900">
                        {log.project?.name || 'Unknown Project'}
                      </h4>
                      <p className="text-sm text-gray-600">
                        {formatTime(log.startTime)} - {log.endTime ? formatTime(log.endTime) : 'Ongoing'}
                      </p>
                      <div className="flex items-center space-x-4 mt-1">
                        <div className="flex items-center text-xs text-gray-500">
                          <ClockIcon className="h-3 w-3 mr-1" />
                          {formatDuration(log.startTime, log.endTime)}
                        </div>
                        {log.location && (
                          <div className="flex items-center text-xs text-gray-500">
                            <MapPinIcon className="h-3 w-3 mr-1" />
                            Location tracked
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(log.status)}`}>
                      {log.status}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <ClockIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No time logs yet</p>
                <p className="text-gray-400">Start tracking your time to see logs here.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Project Selection Modal */}
      {showProjectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Select Project</h3>
              <button
                onClick={() => setShowProjectModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            
            <div className="space-y-4">
              <p className="text-sm text-gray-600 mb-4">
                Please select a project to start time tracking:
              </p>
              
              <select
                value={selectedProject}
                onChange={(e) => setSelectedProject(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              >
                <option value="">Select a project</option>
                {projects.map((project) => (
                  <option key={project._id} value={project._id}>
                    {project.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowProjectModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                onClick={startTracking}
                disabled={!selectedProject}
                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Start Tracking
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Break Modal */}
      {showBreakModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Start Break</h3>
              <button
                onClick={() => setShowBreakModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Break Type
                </label>
                <select
                  value={breakType}
                  onChange={(e) => setBreakType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="lunch">🍽️ Lunch Break</option>
                  <option value="coffee">☕ Coffee Break</option>
                  <option value="rest">😴 Rest Break</option>
                  <option value="other">⏸️ Other</option>
                </select>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center space-x-2">
                  <span className="text-2xl">{getBreakTypeIcon(breakType)}</span>
                  <div>
                    <p className="font-medium text-gray-900">
                      {breakType === 'lunch' ? 'Lunch Break' :
                       breakType === 'coffee' ? 'Coffee Break' :
                       breakType === 'rest' ? 'Rest Break' : 'Other Break'}
                    </p>
                    <p className="text-sm text-gray-600">
                      {breakType === 'lunch' ? '30 minutes recommended' :
                       breakType === 'coffee' ? '15 minutes recommended' :
                       breakType === 'rest' ? '10 minutes recommended' : 'Custom duration'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowBreakModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                onClick={startBreak}
                className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors duration-200"
              >
                Start Break
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimeTracker; 